import { createRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Route as TenantLayoutRoute } from "./_layout";
import { Card, CardBody, CardHeader } from "@/components/application/card/card";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Modal } from "@/components/application/modals/modal-controlled";
import { Button } from "@/components/base/buttons/button";
import { QrDisplay } from "../../components/custom/QrDisplay";
import { PollingStatusBadge } from "../../components/custom/PollingStatusBadge";
import { PaymentDrillDown } from "../../components/custom/PaymentDrillDown";
import { BottomSheet } from "../../components/custom/BottomSheet";
import { usePaymentIntent } from "../../hooks/usePaymentQueries";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { formatKcPayment } from "../../schemas/payment";

/**
 * UC-004 Pay detail route — mobile-first QR display + drill-down kalkulace
 * (Phase 19, REQ-API-003 + REQ-BIZ-017 + REQ-PERF-004 mobile TTI < 3s na 4G).
 *
 * Layout (per UI_WIREFRAME Milan/Zdeněk mobile flow):
 *   1. Title + month + PollingStatusBadge (30s refetch — princip #4)
 *   2. Total amount + EUR advisory (pokud EUR-only měsíc)
 *   3. <QrDisplay> — PNG + Disclosure manuální údaje + Copy buttons
 *   4. "Z čeho se částka skládá" trigger → Modal (desktop) / BottomSheet (mobile)
 *
 * Per princip #3 fair-split transparency — kalkulace VŽDY dostupná
 * (sbalená OK, ale nikdy skrytá). Per princip #2 — žádný FX přepočet.
 *
 * Web Share API NEimplementováno (REQ-BIZ-043 post-MVP).
 */
export const Route = createRoute({
  getParentRoute: () => TenantLayoutRoute,
  path: "/tenant/pay/$paymentId",
  component: PayDetail,
});

function PayDetail() {
  const { paymentId } = Route.useParams();
  const { t } = useTranslation("tenant");
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === "mobile";

  const query = usePaymentIntent(paymentId);
  const [drillDownOpen, setDrillDownOpen] = useState<boolean>(false);

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-4" data-testid="pay-detail-loading">
        <LoadingIndicator />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="flex flex-col gap-4" data-testid="pay-detail-error">
        <h1 className="m-0 text-2xl font-bold text-primary">{t("payment.title")}</h1>
        <InlineAlert variant="error" title="Úhradu nelze načíst">
          <p>
            Zkontrolujte odkaz nebo se přihlaste znovu. Pokud problém přetrvává,
            obraťte se na správce. Payment ID: <code>{paymentId}</code>
          </p>
        </InlineAlert>
      </div>
    );
  }

  const intent = query.data;
  const isPaid = intent.status === "paid";
  const isEurOnly = intent.qr_png_base64 === null;

  const drillDownContent = (
    <PaymentDrillDown
      amountKc={intent.amount_kc}
      amountEur={intent.amount_eur}
      eurAdvisoryFlag={intent.eur_advisory_flag}
      billingGroupCode={intent.billing_group_code}
      drillDown={intent.drill_down}
    />
  );

  return (
    <div className="flex flex-col gap-4" data-testid="pay-detail">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h1 className="m-0 text-2xl font-bold text-primary">
            {t("payment.detailTitle")} — {intent.month}
          </h1>
          <p className="m-0 text-tertiary">
            {t("payment.title")} • {intent.billing_group_code}
          </p>
        </div>
        <PollingStatusBadge
          status={intent.status}
          lastFetchedAt={query.dataUpdatedAt}
        />
      </header>

      {isPaid && intent.paid_at && (
        <InlineAlert variant="success" title="Úhrada zaplacena">
          <p>
            ✓ {t("payment.alreadyPaid", { paidAt: new Date(intent.paid_at).toLocaleString("cs-CZ") })}
          </p>
        </InlineAlert>
      )}

      <Card>
        <CardHeader title={`${t("payment.amount")} — ${formatKcPayment(intent.amount_kc)}`} />
        <CardBody>
          <QrDisplay
            pngBase64={intent.qr_png_base64}
            iban={intent.iban_cz}
            variableSymbol={intent.variable_symbol}
            amountKc={intent.amount_kc}
            message={intent.message}
            billingGroupCode={intent.billing_group_code}
          />
        </CardBody>
      </Card>

      <Button
        type="button"
        color="tertiary"
        className="self-start"
        onPress={() => setDrillDownOpen(true)}
        data-testid="pay-detail-drilldown-trigger"
        aria-haspopup="dialog"
        aria-expanded={drillDownOpen}
      >
        {t("payment.drilldown")}
      </Button>

      {/* Mobile = BottomSheet, Desktop/tablet = Modal */}
      {isMobile ? (
        <BottomSheet
          open={drillDownOpen}
          title={t("payment.actionCard.drillDownTitle")}
          onClose={() => setDrillDownOpen(false)}
        >
          {drillDownContent}
        </BottomSheet>
      ) : (
        <Modal
          open={drillDownOpen}
          title={t("payment.actionCard.drillDownTitle")}
          onClose={() => setDrillDownOpen(false)}
        >
          {drillDownContent}
        </Modal>
      )}

      {isEurOnly && (
        <p className="m-0 text-sm text-tertiary" data-testid="pay-detail-eur-only-note">
          {t("payment.eurOnly.note")}
        </p>
      )}
    </div>
  );
}
